import type Phaser from "phaser";
import { PUDDLE_CELL_PX, RIM_CUT, type Puddle } from "odyssey-shared";

/**
 * The pixels of puddles on the page: a puddle's water painted into its pooled canvas at a threshold, and the ripple rings'
 * frames.
 */

export const RIPPLE_KEY = "weather:ripples";
export const RIPPLE_FRAMES = 6;
export const RIPPLE_W = 28;
export const RIPPLE_H = 16;
export const RIPPLE_RX = 12;
export const RIPPLE_RY = 6;
export const HASH_A = 0x9e3779b1;
const RIM_REACH = 0.18;

/**
 * The colours of a puddle as red, green, blue and alpha bytes: the water, which lets the ground show through, its deeper
 * shade, the sheen along the edges that face the sky, the streaks and the soaked rim round it. Other painters of water that
 * must read as these puddles do (the drip under a Drenched character) take them from here.
 */
export const PUDDLE_COLOURS = { water: [66, 118, 180, 178], deep: [52, 98, 156, 194], sheen: [186, 218, 234, 215], streak: [120, 170, 214, 190], rim: [26, 32, 30, 170] } as const;

const WATER = PUDDLE_COLOURS.water;
const WATER_DEEP = PUDDLE_COLOURS.deep;
const SHEEN = PUDDLE_COLOURS.sheen;
const STREAK = PUDDLE_COLOURS.streak;
const RIM = PUDDLE_COLOURS.rim;
const RIM_ALPHA = PUDDLE_COLOURS.rim[3];

/**
 * Paints a puddle's field at a threshold into pixels: the water, its shade and streaks, a highlight along the edges that face
 * the sky and a soaked rim.
 *
 * @param puddle The puddle's id, which seeds its streaks and sheen, and its side in cells (`radius`).
 * @param cells Its field, `radius` by `radius` cells (`fieldInto`).
 * @param threshold The level the water is cut at (`cutOf`).
 * @param pixels The pixels to paint, at least `radius * PUDDLE_CELL_PX` square; cleared first.
 */
export function paintPuddleInto(puddle: Pick<Puddle, "id" | "radius">, cells: Float32Array, threshold: number, pixels: ImageData): void {
  const side = puddle.radius;
  const width = pixels.width;
  const data = pixels.data;
  const faint = Math.max(RIM_CUT, threshold - RIM_REACH);
  const inside = (cx: number, cy: number): boolean => cx >= 0 && cy >= 0 && cx < side && cy < side && (cells[cy * side + cx] as number) > threshold;
  data.fill(0);
  for (let cy = 0; cy < side; cy += 1) {
    for (let cx = 0; cx < side; cx += 1) {
      const v = cells[cy * side + cx] as number;
      if (v <= faint) continue;
      const roll = Math.imul((cx + puddle.id * 31) ^ Math.imul(cy, HASH_A), HASH_A) >>> 24;
      let colour: readonly number[];
      if (v > threshold) {
        const lit = (!inside(cx, cy - 1) || !inside(cx + 1, cy - 1) || !inside(cx + 1, cy) || !inside(cx, cy - 2)) && roll > 90;
        const bank = !inside(cx - 1, cy) || !inside(cx, cy + 1) || !inside(cx - 1, cy + 1);
        colour = lit ? SHEEN : bank ? WATER_DEEP : roll < 22 && cy % 2 === 0 ? STREAK : roll > 215 ? WATER_DEEP : WATER;
      } else {
        const t = (v - faint) / Math.max(1e-6, threshold - faint);
        colour = [RIM[0], RIM[1], RIM[2], Math.round(RIM_ALPHA * t ** 1.4)];
      }
      for (let dy = 0; dy < PUDDLE_CELL_PX; dy += 1) {
        for (let dx = 0; dx < PUDDLE_CELL_PX; dx += 1) {
          const at = ((cy * PUDDLE_CELL_PX + dy) * width + cx * PUDDLE_CELL_PX + dx) * 4;
          data[at] = colour[0] as number;
          data[at + 1] = colour[1] as number;
          data[at + 2] = colour[2] as number;
          data[at + 3] = colour[3] as number;
        }
      }
    }
  }
}

/**
 * What painting needs of a slot: its canvas, its pixels and the field of the puddle in it.
 */
export interface PaintSlot {
  readonly texture: Phaser.Textures.CanvasTexture;
  readonly pixels: ImageData;
  readonly cells: Float32Array;
}

/**
 * Paints a puddle into its slot at a threshold: the water, its shade and streaks, a highlight along the top edge and a soaked
 * rim, then refreshes the slot's texture.
 *
 * @param puddle The puddle.
 * @param slot The slot painted: its cells hold the puddle's field.
 * @param threshold The cut the water lies above.
 */
export function paintPuddle(puddle: Puddle, slot: PaintSlot, threshold: number): void {
  paintPuddleInto(puddle, slot.cells, threshold, slot.pixels);
  const context = slot.texture.context;
  context.clearRect(0, 0, slot.pixels.width, slot.pixels.height);
  context.putImageData(slot.pixels, 0, 0);
  slot.texture.refresh();
}


/**
 * Draws the ripple frames once per scene: six pale rings that grow and fade, as ellipses of two to one, one frame beside
 * the next.
 *
 * @param scene The scene whose textures hold them.
 */
export function drawRippleTexture(scene: Phaser.Scene): void {
  const textures = scene.textures;
  if (textures.exists(RIPPLE_KEY)) return;
  const texture = textures.createCanvas(RIPPLE_KEY, RIPPLE_W * RIPPLE_FRAMES, RIPPLE_H) as Phaser.Textures.CanvasTexture;
  const image = new ImageData(RIPPLE_W * RIPPLE_FRAMES, RIPPLE_H);
  for (let frame = 0; frame < RIPPLE_FRAMES; frame += 1) {
    const grow = (frame + 1) / RIPPLE_FRAMES;
    const alpha = 235 * (1 - 0.8 * (frame / (RIPPLE_FRAMES - 1)) ** 1.4);
    for (const ring of [1, frame >= 2 ? 0.55 : 0]) {
      if (ring === 0) continue;
      const rx = Math.max(1.5, RIPPLE_RX * grow * ring);
      const ry = Math.max(0.8, RIPPLE_RY * grow * ring);
      for (let step = 0; step < 720; step += 1) {
        const angle = (step / 720) * Math.PI * 2;
        const x = Math.round(RIPPLE_W / 2 + Math.cos(angle) * rx) + frame * RIPPLE_W;
        const y = Math.round(RIPPLE_H / 2 + Math.sin(angle) * ry);
        if (x < 0 || y < 0 || y >= RIPPLE_H) continue;
        const at = (y * image.width + x) * 4;
        image.data[at] = 214;
        image.data[at + 1] = 234;
        image.data[at + 2] = 246;
        image.data[at + 3] = Math.max(image.data[at + 3] as number, alpha * (ring === 1 ? 1 : 0.7));
      }
    }
  }
  texture.context.putImageData(image, 0, 0);
  texture.refresh();
  for (let frame = 0; frame < RIPPLE_FRAMES; frame += 1) texture.add(frame, 0, frame * RIPPLE_W, 0, RIPPLE_W, RIPPLE_H);
}
